import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import '../../../patientTransport/Control/components/map.css'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddLocationAlt,
  CenterFocusStrong,
  Forum,
  Image,
  Layers,
  LocalHospital,
  LocationOff,
  MyLocation,
  NotificationsActive,
  OpenWith,
  ReportProblem,
  Room,
  Route,
  Search,
  Straighten,
  UploadFile,
  Visibility,
  VisibilityOff,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material'
import API from '../../../../services/API'
import EventMapManager from './EventMapManager'
import GpxRouteManager from './GpxRouteManager'
import { GPX_ROUTE_COLORS, formatRouteDistance, kilometreMarkers } from './gpx'
import { MapPinDialog, MapPinMarker, DEFAULT_PIN_COLOR } from './MapPins'
import { ResourceIcon, resolveResourceIconKey } from './resourceIcons'
import {
  eventMapCorners,
  eventMapLngLatBounds,
  eventMapToLngLat,
  hasScaledEventMap,
  lngLatToEventMap,
} from './eventMapGeo'

const OVERLAY_SOURCE = 'event-map-overlay'
const GPX_SOURCE = 'gpx-routes'

// Marker elements host their own React roots; unmounting one synchronously
// from inside an effect/cleanup is unmounting during React's commit, which
// React 19 warns about — let the current commit finish first.
const deferUnmount = (el) => {
  const root = el?._root
  if (!root) return
  el._root = null
  setTimeout(() => root.unmount(), 0)
}
import { ControlRoomContext } from './ControlPage'
import { eventAddressCoordinates } from '../../../../helpers/eventAddress'

const defaultCenter = [-1.5, 52.5]
// Starting view when the room has an event address but nothing plotted yet —
// tight enough to show the venue and its approaches.
const EVENT_CENTRE_ZOOM = 14

// Event maps upload at up to ~8k px on the long edge (see
// helpers/eventMapImage.js), so deep zoom into site-plan detail stays sharp
// well past the old 10x cap.
const EVENT_MAP_MAX_ZOOM = 40

const statusColors = {
  Available: '#00964C',
  'Mobile & Available': '#157746',
  Assigned: '#E32525',
  Acknowledged: '#C2410C',
  'On Route': '#F28B1F',
  'At Scene': '#CCA43B',
  'On Scene': '#CCA43B',
  Break: '#1F3D7C',
  Unavailable: '#85276A',
  'Off Duty': '#475569',
}

const priorityColors = {
  Critical: '#85276A',
  High: '#E32525',
  Moderate: '#F28B1F',
  Low: '#00964C',
}

const hexToRgba = (hex, alpha) => {
  const value = hex.replace('#', '')
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r},${g},${b},${alpha})`
}

const parseCoordinatePair = (value) => {
  if (!value) return null

  if (Array.isArray(value) && value.length >= 2) {
    const lng = Number(value[0])
    const lat = Number(value[1])
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null
  }

  if (typeof value === 'object') {
    return parseCoordinatePair(value.coordinates)
  }

  const parts = String(value)
    .trim()
    .split(/[,\s]+/)
    .map(Number)
    .filter(Number.isFinite)

  if (parts.length < 2) return null

  const [first, second] = parts
  if (Math.abs(first) <= 180 && Math.abs(second) <= 90) {
    return [first, second]
  }

  if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
    return [second, first]
  }

  return null
}

const incidentReference = (incident) =>
  String(incident.incidentNumber ?? incident.number ?? '').slice(-4) || '----'

const markerLabelSx = {
  position: 'absolute',
  bottom: 'calc(100% + 5px)',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '3px 8px',
  borderRadius: '6px',
  backgroundColor: 'rgba(15,23,42,0.94)',
  border: '1px solid rgba(255,255,255,0.22)',
  boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  opacity: 0,
  transition: 'opacity 120ms ease',
}

// Same rule as the PTS dispatch map: a GPS position that hasn't been updated
// for 5 minutes may not be where the resource is now (dead phone, sharing
// ended), so the pin is dimmed with a dashed ring. Site-plan positions placed
// by hand have no age and are never dimmed.
const STALE_LOCATION_THRESHOLD_MS = 5 * 60 * 1000

const getLocationAgeMinutes = (resource) => {
  if (!resource?.locationAt) return null
  const ageMs = Date.now() - new Date(resource.locationAt).getTime()
  return Number.isFinite(ageMs) ? ageMs / 60000 : null
}

const isLocationStale = (resource) => {
  const minutes = getLocationAgeMinutes(resource)
  return minutes !== null && minutes * 60000 > STALE_LOCATION_THRESHOLD_MS
}

const ResourceMarker = ({ resource, color, selected, stale = false }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: stale ? 0.55 : 1,
      '&:hover .cad-marker-label': { opacity: 1 },
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(15,23,42,0.92)',
        border: `2px ${stale ? 'dashed' : 'solid'} ${color}`,
        boxShadow: selected
          ? `0 0 0 2px white, 0 0 12px ${hexToRgba(color, 0.72)}, 0 4px 10px rgba(0,0,0,0.4)`
          : '0 4px 10px rgba(0,0,0,0.4)',
      }}
    >
      <ResourceIcon
        iconKey={resolveResourceIconKey(resource)}
        size={16}
        color={color}
      />
    </Box>
    <Box
      className="cad-marker-label"
      sx={{ ...markerLabelSx, ...(selected && { opacity: 1 }) }}
    >
      <Typography
        sx={{
          color: 'white',
          fontSize: '0.68rem',
          fontWeight: 900,
          lineHeight: 1,
        }}
        noWrap
      >
        {resource.callSign}
      </Typography>
    </Box>
  </Box>
)

const IncidentMarker = ({ incident, color, selected }) => (
  <Box
    sx={{
      position: 'relative',
      width: 34,
      height: 34,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover .cad-marker-label': { opacity: 1 },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        width: 34,
        height: 34,
        borderRadius: '50%',
        backgroundColor: hexToRgba(color, 0.45),
        pointerEvents: 'none',
        animation: 'cadIncidentPulse 3s ease-out infinite',
        '@keyframes cadIncidentPulse': {
          '0%': { transform: 'scale(0.5)', opacity: 0.55 },
          '70%': { transform: 'scale(1.9)', opacity: 0 },
          '100%': { transform: 'scale(1.9)', opacity: 0 },
        },
      }}
    />
    {selected && (
      <Box
        sx={{
          position: 'absolute',
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: `0 0 12px ${hexToRgba(color, 0.72)}`,
          pointerEvents: 'none',
        }}
      />
    )}
    <ReportProblem
      sx={{
        fontSize: 26,
        color,
        filter:
          'drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 2px 4px rgba(0,0,0,0.45))',
      }}
    />
    <Box
      className="cad-marker-label"
      sx={{ ...markerLabelSx, ...(selected && { opacity: 1 }) }}
    >
      <Typography
        sx={{
          color: 'white',
          fontSize: '0.68rem',
          fontWeight: 950,
          lineHeight: 1,
        }}
        noWrap
      >
        {incidentReference(incident)}
      </Typography>
    </Box>
  </Box>
)

const treatmentStatusColors = {
  Open: '#16A34A',
  Standby: '#CA8A04',
  Closed: '#64748B',
}

// Icon-only marker (status-coloured cross, no circle or permanent label) so
// static site markers stay quieter than the resources working around them —
// the name/status show in the shared hover label instead.
const TreatmentCentreMarker = ({ centre }) => {
  const color = treatmentStatusColors[centre.status] || treatmentStatusColors.Standby

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover .cad-marker-label': { opacity: 1 },
      }}
    >
      <LocalHospital
        sx={{
          fontSize: 26,
          color,
          filter:
            'drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 2px 4px rgba(0,0,0,0.45))',
        }}
      />
      <Box
        className="cad-marker-label"
        sx={{ ...markerLabelSx, maxWidth: 180 }}
      >
        <Typography
          sx={{
            color: 'white',
            fontSize: '0.68rem',
            fontWeight: 900,
            lineHeight: 1.2,
          }}
          noWrap
        >
          {centre.name}
        </Typography>
        <Typography
          sx={{
            color,
            fontSize: '0.6rem',
            fontWeight: 800,
            lineHeight: 1.2,
          }}
          noWrap
        >
          {centre.status || 'Standby'}
        </Typography>
      </Box>
    </Box>
  )
}

const EventControlMap = ({
  resources = [],
  incidents = [],
  mapPins = [],
  mapView = 'map',
  eventMap,
  controlRoomID,
  canManageEventMap = false,
  onEventMapSaved,
  gpxRoutes = [],
  onGpxRoutesSaved,
  treatmentCentres = [],
  onOpenTreatmentCentre,
  compact = false,
  height = '100%',
}) => {
  const {
    setSelectedIncident,
    openResourceChat,
    chatSummaries = [],
    refresh,
    canDispatch = true,
    linkedEvent,
  } = useContext(ControlRoomContext)
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const resourceMarkersRef = useRef(new globalThis.Map())
  const incidentMarkersRef = useRef(new globalThis.Map())
  const eventMapBoxRef = useRef(null)
  const eventMapImageRef = useRef(null)
  const eventMapDragRef = useRef(null)
  const eventMapPanRef = useRef(null)
  const hasFitRef = useRef(false)
  const eventCentre = eventAddressCoordinates(linkedEvent?.eventAddress)
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [eventMapUrl, setEventMapUrl] = useState(null)
  const [eventMapLoading, setEventMapLoading] = useState(false)
  const [openEventMapManager, setOpenEventMapManager] = useState(false)
  const [armedResourceID, setArmedResourceID] = useState(null)
  const [unlockedResourceID, setUnlockedResourceID] = useState(null)
  const [manualResourcePositions, setManualResourcePositions] = useState({})
  const [eventMapImageMetrics, setEventMapImageMetrics] = useState(null)
  const [eventMapViewport, setEventMapViewport] = useState({
    zoom: 1,
    panX: 0,
    panY: 0,
  })
  const [addingPin, setAddingPin] = useState(false)
  const [pinDialog, setPinDialog] = useState(null) // { pin } | { coordinates }
  const [highlightedPinID, setHighlightedPinID] = useState(null)
  const pinMarkersRef = useRef(new globalThis.Map())
  const treatmentMarkersRef = useRef(new globalThis.Map())
  const overlayUrlRef = useRef(null)
  const overlayFittedRef = useRef(false)
  // GPX course lines live on the same geographic map instance; km-marker
  // chips are plain DOM maplibre markers (no React roots to defer-unmount).
  const gpxKmMarkersRef = useRef([])
  const gpxFittedRef = useRef(false)
  const [hiddenGpxRouteIDs, setHiddenGpxRouteIDs] = useState(() => new Set())
  const [openGpxManager, setOpenGpxManager] = useState(false)
  // Overlay = the to-scale site plan laid over the live map; it shares the
  // geographic map instance with the plain 'map' view.
  const scaledEventMap = hasScaledEventMap(eventMap)
  const isScaledOverlay = mapView === 'overlay' && scaledEventMap
  const isGpxView = mapView === 'gpx'
  const isGeographicMap = mapView === 'map' || isGpxView || isScaledOverlay

  const eventMapPins = useMemo(
    () =>
      (mapPins ?? [])
        .filter((pin) => pin.coordinates?.length === 2)
        .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [mapPins],
  )

  // Off-duty resources aren't being deployed, so surfacing them as markers
  // just adds noise a dispatcher has to mentally filter out.
  const onDutyResources = useMemo(
    () => resources.filter((resource) => resource.status !== 'Off Duty'),
    [resources],
  )

  // On a to-scale map a manual site-plan position is a real position, so it
  // stands in for missing GPS on the geographic views.
  const mappedResources = useMemo(
    () =>
      onDutyResources
        .map((resource) => {
          const crewCoordinates = parseCoordinatePair(resource?.location)
          const vehicleCoordinates = parseCoordinatePair(
            resource?.linkedVehicle?.location,
          )
          return {
            ...resource,
            coordinates:
              crewCoordinates ||
              vehicleCoordinates ||
              parseCoordinatePair(resource?.manualCoordinates) ||
              eventMapToLngLat(eventMap, resource?.eventMapCoordinates),
            // When the GPS position was taken; null for site-plan positions
            locationAt: crewCoordinates
              ? resource?.lastLocationUpdate?.at ?? null
              : vehicleCoordinates
                ? resource?.linkedVehicle?.lastLocationUpdate?.at ?? null
                : null,
          }
        })
        .filter((resource) => resource.coordinates),
    [onDutyResources, eventMap],
  )

  const mappedIncidents = useMemo(
    () =>
      incidents
        .map((incident) => ({
          ...incident,
          coordinates:
            parseCoordinatePair(incident.coordinates) ||
            eventMapToLngLat(eventMap, incident.eventMapCoordinates),
        }))
        .filter((incident) => incident.coordinates),
    [incidents, eventMap],
  )

  const mappedPins = useMemo(
    () =>
      (mapPins ?? [])
        .map((pin) => ({
          ...pin,
          lngLat: eventMapToLngLat(eventMap, pin.coordinates),
        }))
        .filter((pin) => pin.lngLat),
    [mapPins, eventMap],
  )

  // Treatment centres carry the same dual position as incidents: a "lng,lat"
  // string and/or 0-1 site-plan fractions (which stand in for GPS on a
  // to-scale map, like everything else).
  const activeTreatmentCentres = useMemo(
    () => (treatmentCentres ?? []).filter((centre) => centre.active !== false),
    [treatmentCentres],
  )

  const mappedTreatmentCentres = useMemo(
    () =>
      activeTreatmentCentres
        .map((centre) => ({
          ...centre,
          lngLat:
            parseCoordinatePair(centre.coordinates) ||
            eventMapToLngLat(eventMap, centre.eventMapCoordinates),
        }))
        .filter((centre) => centre.lngLat),
    [activeTreatmentCentres, eventMap],
  )

  const eventMapTreatmentCentres = useMemo(
    () =>
      activeTreatmentCentres.filter(
        (centre) => centre.eventMapCoordinates?.length === 2,
      ),
    [activeTreatmentCentres],
  )

  const eventMapResources = useMemo(
    () =>
      onDutyResources
        .map((resource) => ({
          ...resource,
          eventMapCoordinates:
            manualResourcePositions[resource.id] ??
            resource.eventMapCoordinates ??
            null,
        }))
        .filter((resource) => resource.eventMapCoordinates?.length === 2),
    [manualResourcePositions, onDutyResources],
  )

  const eventMapIncidents = useMemo(  
    () =>
      incidents.filter(
        (incident) => incident.eventMapCoordinates?.length === 2,
      ),
    [incidents],
  )

  const unmappedResources = onDutyResources.length - mappedResources.length
  const unmappedIncidents = incidents.length - mappedIncidents.length
  const resourceMap = useMemo(
    () => new Map(resources.map((resource) => [resource.id, resource])),
    [resources],
  )

  const unreadByResource = useMemo(() => {
    const map = new Map()
    chatSummaries.forEach((summary) =>
      map.set(String(summary.resource), summary.unreadFromCrew ?? 0),
    )
    return map
  }, [chatSummaries])

  const getEventMapImageBaseMetrics = () => {
    const viewport = eventMapBoxRef.current
    const image = eventMapImageRef.current
    if (!viewport || !image) return null

    const rect = viewport.getBoundingClientRect()
    const imageRatio =
      image.naturalWidth && image.naturalHeight
        ? image.naturalWidth / image.naturalHeight
        : rect.width / rect.height
    const viewportRatio = rect.width / rect.height
    const width =
      imageRatio > viewportRatio ? rect.width : rect.height * imageRatio
    const height =
      imageRatio > viewportRatio ? rect.width / imageRatio : rect.height

    return {
      width,
      height,
      left: (rect.width - width) / 2,
      top: (rect.height - height) / 2,
      viewportWidth: rect.width,
      viewportHeight: rect.height,
    }
  }

  const getEventMapImageMetrics = () => {
    const base = getEventMapImageBaseMetrics()
    if (!base) return null

    return {
      ...base,
      width: base.width * eventMapViewport.zoom,
      height: base.height * eventMapViewport.zoom,
      left: base.left + eventMapViewport.panX,
      top: base.top + eventMapViewport.panY,
      baseWidth: base.width,
      baseHeight: base.height,
      baseLeft: base.left,
      baseTop: base.top,
    }
  }

  const refreshEventMapImageMetrics = () => {
    const metrics = getEventMapImageMetrics()
    if (metrics) setEventMapImageMetrics(metrics)
  }

  useEffect(() => {
    if (mapView !== 'event' || !eventMapUrl) return undefined

    refreshEventMapImageMetrics()
    const viewport = eventMapBoxRef.current
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(refreshEventMapImageMetrics)
    observer.observe(viewport)

    return () => observer.disconnect()
  }, [mapView, eventMapUrl, eventMapViewport])

  useEffect(() => {
    setEventMapViewport({ zoom: 1, panX: 0, panY: 0 })
  }, [eventMap?.s3key, mapView])

  const eventMapPositionSx = (coordinates) => ({
    left: eventMapImageMetrics
      ? eventMapImageMetrics.left + coordinates[0] * eventMapImageMetrics.width
      : `${coordinates[0] * 100}%`,
    top: eventMapImageMetrics
      ? eventMapImageMetrics.top + coordinates[1] * eventMapImageMetrics.height
      : `${coordinates[1] * 100}%`,
  })

  const persistResourceEventMapCoordinates = (resourceID, coordinates) => {
    setManualResourcePositions((prev) => ({
      ...prev,
      [resourceID]: coordinates,
    }))

    API.post('/commandcontrol/updateresource', {
      controlRoomID,
      resourceID,
      changes: {
        eventMapCoordinates: coordinates,
      },
    }).catch(() => {
      setManualResourcePositions((prev) => {
        const next = { ...prev }
        delete next[resourceID]
        return next
      })
    })
  }

  // Manual geographic placement for a resource without live GPS. On a
  // to-scale map the matching plan fraction is written too, so the Event
  // view shows the same spot.
  const placeResourceAtLngLat = (resourceID, lngLat) => {
    const changes = { manualCoordinates: lngLat }
    const fractions = scaledEventMap
      ? lngLatToEventMap(eventMap, lngLat)
      : null
    if (fractions) changes.eventMapCoordinates = fractions

    API.post('/commandcontrol/updateresource', {
      controlRoomID,
      resourceID,
      changes,
    })
      .then((res) => {
        if (res.status === 200) refresh?.()
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (!eventMap?.s3key || !(mapView === 'event' || isScaledOverlay)) {
      setEventMapUrl(null)
      setEventMapLoading(false)
      return
    }

    setEventMapLoading(true)
    API.post('/gets3downloadurl', {
      key: eventMap.s3key,
      inlineView: true,
    })
      .then((res) => setEventMapUrl(res.data))
      .catch(() => setEventMapUrl(null))
      .finally(() => setEventMapLoading(false))
  }, [eventMap?.s3key, mapView === 'event' || isScaledOverlay])

  const fitToMarkers = () => {
    if (!mapRef.current) return

    const bounds = new maplibregl.LngLatBounds()
    let hasPoints = false

    ;[...mappedResources, ...mappedIncidents].forEach((item) => {
      bounds.extend(item.coordinates)
      hasPoints = true
    })
    if (isScaledOverlay) {
      eventMapCorners(eventMap).forEach((corner) => bounds.extend(corner))
      hasPoints = true
    }

    if (!hasPoints) {
      if (eventCentre) {
        mapRef.current.flyTo({ center: eventCentre, zoom: EVENT_CENTRE_ZOOM })
      } else {
        mapRef.current.flyTo({ center: defaultCenter, zoom: 6 })
      }
      return
    }

    mapRef.current.fitBounds(bounds, {
      padding: compact
        ? { top: 62, right: 24, bottom: 24, left: 24 }
        : { top: 92, right: 360, bottom: 48, left: 48 },
      maxZoom: 15,
      duration: 700,
    })
  }

  const focusResource = (resource) => {
    const coordinates =
      parseCoordinatePair(resource?.location) ||
      parseCoordinatePair(resource?.linkedVehicle?.location) ||
      parseCoordinatePair(resource?.manualCoordinates) ||
      eventMapToLngLat(eventMap, resource?.eventMapCoordinates)

    setSelectedMarker({ type: 'resource', id: resource.id })

    if (coordinates && mapRef.current) {
      mapRef.current.flyTo({ center: coordinates, zoom: 17, duration: 700 })
      return
    }

    // No geographic position at all: on the map views, arm click-to-place
    // for a manual position (the event view arms for the site plan below).
    if (isGeographicMap && canDispatch) {
      setArmedResourceID(resource.id)
      return
    }

    if (resource.eventMapCoordinates?.length === 2) {
      setArmedResourceID(null)
      return
    }

    if (mapView === 'event') {
      setArmedResourceID(resource.id)
    }
  }

  const getEventMapCoordinatesFromPointer = (event) => {
    const rect = eventMapBoxRef.current?.getBoundingClientRect()
    const metrics = getEventMapImageMetrics()
    if (!rect || !metrics) return null

    const x = Number(
      Math.min(
        1,
        Math.max(0, (event.clientX - rect.left - metrics.left) / metrics.width),
      ).toFixed(4),
    )
    const y = Number(
      Math.min(
        1,
        Math.max(0, (event.clientY - rect.top - metrics.top) / metrics.height),
      ).toFixed(4),
    )

    return [x, y]
  }

  // `nextZoom` is either an absolute zoom level, or (with `relative: true`) a
  // factor applied to the zoom at the time this update actually runs. The
  // relative form matters for the wheel handler below: a burst of wheel
  // events can fire faster than React re-renders, and reading
  // `eventMapViewport.zoom` from the outer closure would apply every delta
  // against the same stale value instead of compounding them.
  const updateEventMapZoom = (nextZoom, origin, { relative = false } = {}) => {
    const base = getEventMapImageBaseMetrics()
    const rect = eventMapBoxRef.current?.getBoundingClientRect()
    if (!base || !rect) return

    setEventMapViewport((prev) => {
      const target = relative ? prev.zoom * nextZoom : nextZoom
      const zoom = Math.min(EVENT_MAP_MAX_ZOOM, Math.max(1, Number(target)))
      const currentWidth = base.width * prev.zoom
      const currentHeight = base.height * prev.zoom
      const currentLeft = base.left + prev.panX
      const currentTop = base.top + prev.panY
      const originX = origin?.x ?? rect.width / 2
      const originY = origin?.y ?? rect.height / 2
      const ratioX = currentWidth
        ? (originX - currentLeft) / currentWidth
        : 0.5
      const ratioY = currentHeight
        ? (originY - currentTop) / currentHeight
        : 0.5

      return {
        zoom,
        panX: originX - base.left - ratioX * base.width * zoom,
        panY: originY - base.top - ratioY * base.height * zoom,
      }
    })
  }

  const handleEventMapWheel = (event) => {
    if (!eventMapUrl) return
    event.preventDefault()
    const rect = eventMapBoxRef.current?.getBoundingClientRect()
    if (!rect) return
    const factor = event.deltaY > 0 ? 0.88 : 1.12

    updateEventMapZoom(
      factor,
      { x: event.clientX - rect.left, y: event.clientY - rect.top },
      { relative: true },
    )
  }

  // React delegates wheel listeners to the root as passive by default, so
  // event.preventDefault() inside a JSX onWheel handler is silently ignored —
  // the browser's native page scroll / pinch-zoom still runs alongside our
  // own zoom logic, which is what makes wheel/trackpad zoom over the map
  // fight with the page. Attaching the listener natively with
  // { passive: false } lets preventDefault actually suppress the browser
  // default so only the map zooms.
  useEffect(() => {
    if (mapView !== 'event') return undefined
    const node = eventMapBoxRef.current
    if (!node) return undefined

    node.addEventListener('wheel', handleEventMapWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleEventMapWheel)
  }, [mapView, eventMapUrl])

  const handleEventMapPointerDown = (event) => {
    if (
      !eventMapUrl ||
      armedResourceID ||
      addingPin ||
      event.button !== 0 ||
      event.target.closest?.('[data-event-map-marker="true"]') ||
      event.target.closest?.('[data-event-map-control="true"]')
    ) {
      return
    }

    event.currentTarget.setPointerCapture?.(event.pointerId)
    eventMapPanRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: eventMapViewport.panX,
      panY: eventMapViewport.panY,
    }
  }

  const handleEventMapPointerMove = (event) => {
    const pan = eventMapPanRef.current
    if (!pan || pan.pointerId !== event.pointerId) return

    setEventMapViewport((prev) => ({
      ...prev,
      panX: pan.panX + event.clientX - pan.startX,
      panY: pan.panY + event.clientY - pan.startY,
    }))
  }

  const handleEventMapPointerUp = (event) => {
    const pan = eventMapPanRef.current
    if (!pan || pan.pointerId !== event.pointerId) return
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    eventMapPanRef.current = null
  }

  const placeArmedResource = (event) => {
    if (addingPin) {
      const coordinates = getEventMapCoordinatesFromPointer(event)
      if (!coordinates) return
      setPinDialog({ coordinates })
      setAddingPin(false)
      return
    }
    if (!armedResourceID) return
    const coordinates = getEventMapCoordinatesFromPointer(event)
    if (!coordinates) return
    persistResourceEventMapCoordinates(armedResourceID, coordinates)
    setSelectedMarker({ type: 'resource', id: armedResourceID })
    setUnlockedResourceID(null)
    setArmedResourceID(null)
  }

  // Centre the event map viewport on a 0-1 coordinate pair at (at least) the
  // given zoom — used when a searched pin is selected.
  const focusEventMapPoint = (coordinates, targetZoom = 4) => {
    const base = getEventMapImageBaseMetrics()
    const rect = eventMapBoxRef.current?.getBoundingClientRect()
    if (!base || !rect) return
    const zoom = Math.min(
      EVENT_MAP_MAX_ZOOM,
      Math.max(1, Math.max(targetZoom, eventMapViewport.zoom)),
    )

    setEventMapViewport({
      zoom,
      panX: rect.width / 2 - base.left - coordinates[0] * base.width * zoom,
      panY: rect.height / 2 - base.top - coordinates[1] * base.height * zoom,
    })
  }

  const focusPin = (pin) => {
    if (!pin?.coordinates) return
    setHighlightedPinID(pin.id)
    if (isGeographicMap) {
      const lngLat = eventMapToLngLat(eventMap, pin.coordinates)
      if (lngLat && mapRef.current) {
        mapRef.current.flyTo({ center: lngLat, zoom: 18, duration: 700 })
      }
      return
    }
    focusEventMapPoint(pin.coordinates)
  }

  const savePinDialog = async ({ label, color }) => {
    const isEdit = !!pinDialog?.pin?.id
    const url = isEdit
      ? `/controlrooms/${controlRoomID}/map-pins/${pinDialog.pin.id}`
      : `/controlrooms/${controlRoomID}/map-pins`
    const body = isEdit
      ? { label, color }
      : { label, color, coordinates: pinDialog?.coordinates }

    const res = await API.post(url, body)
    if (res.status === 200) {
      setPinDialog(null)
      refresh?.()
    }
  }

  const deletePinDialog = async () => {
    if (!pinDialog?.pin?.id) return
    const res = await API.post(
      `/controlrooms/${controlRoomID}/map-pins/${pinDialog.pin.id}/delete`,
    )
    if (res.status === 200) {
      setHighlightedPinID(null)
      setPinDialog(null)
      refresh?.()
    }
  }

  const handleResourceDrop = (event) => {
    event.preventDefault()
    const resourceID = event.dataTransfer.getData('text/resource-id')
    if (!resourceID) return
    const coordinates = getEventMapCoordinatesFromPointer(event)
    if (!coordinates) return
    persistResourceEventMapCoordinates(resourceID, coordinates)
    setSelectedMarker({ type: 'resource', id: resourceID })
    setUnlockedResourceID(null)
    setArmedResourceID(null)
  }

  const handleEventResourcePointerDown = (event, resource) => {
    if (unlockedResourceID !== resource.id) return
    event.stopPropagation()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    eventMapDragRef.current = {
      pointerId: event.pointerId,
      resourceID: resource.id,
      dragged: false,
    }
  }

  const handleEventResourcePointerMove = (event) => {
    const drag = eventMapDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const coordinates = getEventMapCoordinatesFromPointer(event)
    if (!coordinates) return
    drag.dragged = true
    setManualResourcePositions((prev) => ({
      ...prev,
      [drag.resourceID]: coordinates,
    }))
  }

  const handleEventResourcePointerUp = (event) => {
    const drag = eventMapDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    const coordinates = getEventMapCoordinatesFromPointer(event)
    if (coordinates && drag.dragged) {
      persistResourceEventMapCoordinates(drag.resourceID, coordinates)
    }
    eventMapDragRef.current = null
  }

  useEffect(() => {
    if (mapRef.current) return

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center: defaultCenter,
      zoom: 6,
      attributionControl: true,
    })

    map.addControl(new maplibregl.NavigationControl(), 'bottom-left')
    mapRef.current = map

    return () => {
      resourceMarkersRef.current.forEach(({ marker, el }) => {
        marker.remove()
        deferUnmount(el)
      })
      incidentMarkersRef.current.forEach(({ marker, el }) => {
        marker.remove()
        deferUnmount(el)
      })
      pinMarkersRef.current.forEach(({ marker, el }) => {
        marker.remove()
        deferUnmount(el)
      })
      treatmentMarkersRef.current.forEach(({ marker, el }) => {
        marker.remove()
        deferUnmount(el)
      })
      treatmentMarkersRef.current.clear()
      resourceMarkersRef.current.clear()
      incidentMarkersRef.current.clear()
      pinMarkersRef.current.clear()
      gpxKmMarkersRef.current.forEach((marker) => marker.remove())
      gpxKmMarkersRef.current = []
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!isGeographicMap) return
    mapRef.current?.resize()
  }, [isGeographicMap])

  // Site plan laid over the live map (overlay view). Fit to the plan the
  // first time the view opens; leaving the view drops the layer again.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return undefined

    const removeOverlay = () => {
      if (map.getLayer(OVERLAY_SOURCE)) map.removeLayer(OVERLAY_SOURCE)
      if (map.getSource(OVERLAY_SOURCE)) map.removeSource(OVERLAY_SOURCE)
      overlayUrlRef.current = null
    }

    if (!isScaledOverlay || !eventMapUrl) {
      if (!isScaledOverlay) overlayFittedRef.current = false
      if (map.loaded()) removeOverlay()
      return undefined
    }

    const addOverlay = () => {
      if (overlayUrlRef.current !== eventMapUrl) removeOverlay()
      if (!map.getSource(OVERLAY_SOURCE)) {
        map.addSource(OVERLAY_SOURCE, {
          type: 'image',
          url: eventMapUrl,
          coordinates: eventMapCorners(eventMap),
        })
        map.addLayer({
          id: OVERLAY_SOURCE,
          type: 'raster',
          source: OVERLAY_SOURCE,
          paint: { 'raster-opacity': eventMap.opacity ?? 0.7 },
        })
        overlayUrlRef.current = eventMapUrl
      }
      if (!overlayFittedRef.current) {
        overlayFittedRef.current = true
        map.resize()
        map.fitBounds(eventMapLngLatBounds(eventMap), {
          padding: compact
            ? { top: 62, right: 24, bottom: 24, left: 24 }
            : { top: 92, right: 360, bottom: 48, left: 48 },
          duration: 0,
          maxZoom: 18,
        })
      }
    }

    // `loaded()` is false whenever anything is still in flight, and 'load'
    // only ever fires once — waiting on it can miss the boat. The style
    // being ready is all addSource/addLayer need.
    let cancelled = false
    const whenReady = () => {
      if (cancelled) return
      if (map.isStyleLoaded()) addOverlay()
      else map.once('styledata', whenReady)
    }
    whenReady()

    return () => {
      cancelled = true
    }
  }, [isScaledOverlay, eventMapUrl, eventMap?.bounds, eventMap?.opacity])

  // GPX course lines + kilometre chips (gpx view). Same style-ready dance as
  // the overlay effect above; the lines are a canvas layer, the km chips are
  // DOM markers so they always sit above it.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return undefined

    const removeGpx = () => {
      gpxKmMarkersRef.current.forEach((marker) => marker.remove())
      gpxKmMarkersRef.current = []
      if (map.getLayer(GPX_SOURCE)) map.removeLayer(GPX_SOURCE)
      if (map.getSource(GPX_SOURCE)) map.removeSource(GPX_SOURCE)
    }

    const visibleRoutes = (gpxRoutes ?? []).filter(
      (route) =>
        !hiddenGpxRouteIDs.has(String(route._id)) &&
        (route.coordinates?.length ?? 0) >= 2,
    )

    if (!isGpxView || !visibleRoutes.length) {
      if (!isGpxView) gpxFittedRef.current = false
      if (map.loaded()) removeGpx()
      return undefined
    }

    const addGpx = () => {
      // Route edits are rare — remove and re-add rather than diffing setData.
      removeGpx()
      map.addSource(GPX_SOURCE, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: visibleRoutes.map((route) => ({
            type: 'Feature',
            properties: { color: route.color || GPX_ROUTE_COLORS[0] },
            geometry: { type: 'LineString', coordinates: route.coordinates },
          })),
        },
      })
      map.addLayer({
        id: GPX_SOURCE,
        type: 'line',
        source: GPX_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
          'line-opacity': 0.9,
        },
      })

      visibleRoutes.forEach((route) => {
        const color = route.color || GPX_ROUTE_COLORS[0]
        kilometreMarkers(route.coordinates).forEach(({ km, lngLat }) => {
          const el = document.createElement('div')
          el.textContent = String(km)
          Object.assign(el.style, {
            minWidth: '20px',
            height: '20px',
            padding: '0 4px',
            borderRadius: '10px',
            backgroundColor: color,
            color: 'white',
            border: '2px solid white',
            boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: '900',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          })
          const marker = new maplibregl.Marker({
            element: el,
            anchor: 'center',
          })
            .setLngLat(lngLat)
            .addTo(map)
          gpxKmMarkersRef.current.push(marker)
        })
      })

      if (!gpxFittedRef.current) {
        gpxFittedRef.current = true
        map.resize()
        const bounds = new maplibregl.LngLatBounds()
        visibleRoutes.forEach((route) =>
          route.coordinates.forEach((point) => bounds.extend(point)),
        )
        map.fitBounds(bounds, {
          padding: compact
            ? { top: 62, right: 24, bottom: 24, left: 24 }
            : { top: 92, right: 360, bottom: 48, left: 48 },
          duration: 0,
          maxZoom: 16,
        })
      }
    }

    // Unlike the overlay (whose signed-URL fetch re-runs its effect well
    // after the style settles), this effect's only trigger on a remount is
    // the mount itself — and a once('styledata') chain can strand a fresh
    // map: the last styledata can fire while isStyleLoaded() is still false
    // (sprite still inflight emits no further styledata), so the course
    // never appeared after switching screen layouts. Persistent listeners +
    // the 'load' event cover that gap; guarding on the source's presence
    // keeps re-fires idempotent and re-adds after any future style swap.
    let cancelled = false
    const ensure = () => {
      if (cancelled || map.getSource(GPX_SOURCE) || !map.isStyleLoaded()) return
      addGpx()
    }
    map.on('styledata', ensure)
    map.on('load', ensure)
    ensure()

    return () => {
      cancelled = true
      map.off('styledata', ensure)
      map.off('load', ensure)
    }
  }, [isGpxView, gpxRoutes, hiddenGpxRouteIDs, compact])

  // Click-to-place an armed resource on the geographic views: one click
  // stores a manual position, then disarms. Marker elements are DOM nodes
  // above the canvas, so their clicks never reach this handler.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !armedResourceID || !isGeographicMap || !canDispatch) {
      return undefined
    }

    const canvas = map.getCanvas()
    const previousCursor = canvas.style.cursor
    canvas.style.cursor = 'crosshair'

    const handleClick = (event) => {
      placeResourceAtLngLat(armedResourceID, [
        event.lngLat.lng,
        event.lngLat.lat,
      ])
      setArmedResourceID(null)
    }
    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
      canvas.style.cursor = previousCursor
    }
  }, [armedResourceID, isGeographicMap, canDispatch])

  useEffect(() => {
    if (!mapRef.current) return

    const seen = new Set()

    mappedResources.forEach((resource) => {
      const key = resource.id
      seen.add(key)
      const existing = resourceMarkersRef.current.get(key)

      if (existing) {
        existing.marker.setLngLat(resource.coordinates)
        existing.resource = resource
        return
      }

      const el = document.createElement('div')
      const root = createRoot(el)
      el._root = root
      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat(resource.coordinates)
        .addTo(mapRef.current)

      resourceMarkersRef.current.set(key, { marker, el, resource })
    })

    resourceMarkersRef.current.forEach(({ marker, el }, key) => {
      if (seen.has(key)) return
      marker.remove()
      deferUnmount(el)
      resourceMarkersRef.current.delete(key)
    })
  }, [mappedResources])

  useEffect(() => {
    if (!mapRef.current) return

    const seen = new Set()

    mappedIncidents.forEach((incident) => {
      const key = incident.id
      seen.add(key)
      const existing = incidentMarkersRef.current.get(key)

      if (existing) {
        existing.marker.setLngLat(incident.coordinates)
        existing.incident = incident
        return
      }

      const el = document.createElement('div')
      const root = createRoot(el)
      el._root = root
      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat(incident.coordinates)
        .addTo(mapRef.current)

      incidentMarkersRef.current.set(key, { marker, el, incident })
    })

    incidentMarkersRef.current.forEach(({ marker, el }, key) => {
      if (seen.has(key)) return
      marker.remove()
      deferUnmount(el)
      incidentMarkersRef.current.delete(key)
    })
  }, [mappedIncidents])

  // Map pins on the geographic views (only possible on a to-scale map).
  useEffect(() => {
    if (!mapRef.current) return

    const seen = new Set()

    mappedPins.forEach((pin) => {
      seen.add(pin.id)
      const existing = pinMarkersRef.current.get(pin.id)

      if (existing) {
        existing.marker.setLngLat(pin.lngLat)
        existing.pin = pin
        return
      }

      const el = document.createElement('div')
      const root = createRoot(el)
      el._root = root
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(pin.lngLat)
        .addTo(mapRef.current)

      pinMarkersRef.current.set(pin.id, { marker, el, pin })
    })

    pinMarkersRef.current.forEach(({ marker, el }, key) => {
      if (seen.has(key)) return
      marker.remove()
      deferUnmount(el)
      pinMarkersRef.current.delete(key)
    })
  }, [mappedPins])

  useEffect(() => {
    pinMarkersRef.current.forEach(({ el, pin }) => {
      const highlighted = highlightedPinID === pin.id
      el.style.cursor = 'pointer'
      el.style.zIndex = highlighted ? '2' : '0'
      el._root.render(<MapPinMarker pin={pin} highlighted={highlighted} />)
      el.onclick = () => {
        setHighlightedPinID(pin.id)
        if (canDispatch) setPinDialog({ pin })
      }
    })
  }, [mappedPins, highlightedPinID, canDispatch])

  // Treatment centres on the geographic views, mirroring the pin markers.
  useEffect(() => {
    if (!mapRef.current) return

    const seen = new Set()

    mappedTreatmentCentres.forEach((centre) => {
      const key = String(centre._id)
      seen.add(key)
      const existing = treatmentMarkersRef.current.get(key)

      if (existing) {
        existing.marker.setLngLat(centre.lngLat)
        existing.centre = centre
        return
      }

      const el = document.createElement('div')
      const root = createRoot(el)
      el._root = root
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(centre.lngLat)
        .addTo(mapRef.current)

      treatmentMarkersRef.current.set(key, { marker, el, centre })
    })

    treatmentMarkersRef.current.forEach(({ marker, el }, key) => {
      if (seen.has(key)) return
      marker.remove()
      deferUnmount(el)
      treatmentMarkersRef.current.delete(key)
    })
  }, [mappedTreatmentCentres])

  useEffect(() => {
    treatmentMarkersRef.current.forEach(({ el, centre }) => {
      el.style.cursor = onOpenTreatmentCentre ? 'pointer' : 'default'
      el.onmouseenter = () => {
        el.style.zIndex = '3'
      }
      el.onmouseleave = () => {
        el.style.zIndex = ''
      }
      el._root.render(<TreatmentCentreMarker centre={centre} />)
      el.onclick = () => onOpenTreatmentCentre?.(String(centre._id))
    })
  }, [mappedTreatmentCentres, onOpenTreatmentCentre])

  useEffect(() => {
    resourceMarkersRef.current.forEach(({ el, resource }) => {
      const color = statusColors[resource.status] || '#239AA4'
      const selected =
        selectedMarker?.type === 'resource' &&
        selectedMarker?.id === resource.id

      const stale = isLocationStale(resource)
      el.title = stale
        ? `${resource.callSign}: location last updated ${Math.round(getLocationAgeMinutes(resource))} min ago — may not be current`
        : ''
      el.style.cursor = 'pointer'
      el.style.zIndex = selected ? '2' : ''
      el.onmouseenter = () => {
        el.style.zIndex = '3'
      }
      el.onmouseleave = () => {
        el.style.zIndex = selected ? '2' : ''
      }
      el._root.render(
        <ResourceMarker
          resource={resource}
          color={color}
          selected={selected}
          stale={stale}
        />,
      )
      el.onclick = () =>
        setSelectedMarker({ type: 'resource', id: resource.id })
    })

    incidentMarkersRef.current.forEach(({ el, incident }) => {
      const color = priorityColors[incident.priority] || priorityColors.Low
      const selected =
        selectedMarker?.type === 'incident' &&
        selectedMarker?.id === incident.id

      el.style.cursor = 'pointer'
      el.style.zIndex = selected ? '2' : '1'
      el.onmouseenter = () => {
        el.style.zIndex = '3'
      }
      el.onmouseleave = () => {
        el.style.zIndex = selected ? '2' : '1'
      }
      el._root.render(
        <IncidentMarker incident={incident} color={color} selected={selected} />,
      )
      el.onclick = () =>
        setSelectedMarker({ type: 'incident', id: incident.id })
    })
  }, [mappedResources, mappedIncidents, selectedMarker])

  // Nothing plotted yet but the event has an address: open on the venue
  // rather than the whole-country default. Deliberately doesn't mark the map
  // as fitted, so the first resources/incidents still get framed below.
  useEffect(() => {
    if (hasFitRef.current || !eventCentre || !mapRef.current) return
    if (mappedResources.length || mappedIncidents.length) return
    mapRef.current.resize()
    mapRef.current.jumpTo({ center: eventCentre, zoom: EVENT_CENTRE_ZOOM })
  }, [eventCentre?.[0], eventCentre?.[1]])

  useEffect(() => {
    if (hasFitRef.current) return
    if (!mappedResources.length && !mappedIncidents.length) return
    hasFitRef.current = true
    // The container may only just have been laid out — make sure the map
    // knows its real size before framing the markers.
    mapRef.current?.resize()
    fitToMarkers()
  }, [mappedResources.length, mappedIncidents.length])

  const selectedResource =
    selectedMarker?.type === 'resource'
      ? resourceMap.get(selectedMarker.id)
      : null
  const selectedIncident =
    selectedMarker?.type === 'incident'
      ? incidents.find((incident) => incident.id === selectedMarker.id)
      : null
  const selectedResourceEventMapCoordinates = selectedResource
    ? (manualResourcePositions[selectedResource.id] ??
      selectedResource.eventMapCoordinates)
    : null
  const usesManualEventMap = !!eventMap?.s3key && !eventMap.drawnToScale
  // Manual placement on the geographic views only applies while a resource
  // has no live GPS — a crew or vehicle fix always wins over it.
  const selectedResourceLiveGps = selectedResource
    ? parseCoordinatePair(selectedResource.location) ||
      parseCoordinatePair(selectedResource.linkedVehicle?.location)
    : null
  const selectedResourceMappedLngLat = selectedResource
    ? parseCoordinatePair(selectedResource.manualCoordinates) ||
      eventMapToLngLat(eventMap, selectedResource.eventMapCoordinates)
    : null

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height,
        minHeight: compact ? 360 : 560,
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
      }}
    >
      <Box
        ref={mapContainer}
        sx={{
          width: '100%',
          height: '100%',
          display: isGeographicMap ? 'block' : 'none',
        }}
      />

      {mapView === 'event' && (
        <Box
          ref={eventMapBoxRef}
          onClick={placeArmedResource}
          onPointerDown={handleEventMapPointerDown}
          onPointerMove={handleEventMapPointerMove}
          onPointerUp={handleEventMapPointerUp}
          onPointerCancel={handleEventMapPointerUp}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleResourceDrop}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#0F172A',
            cursor:
              armedResourceID || addingPin
                ? 'crosshair'
                : eventMapViewport.zoom > 1
                  ? 'grab'
                  : 'default',
            touchAction: 'none',
          }}
        >
          {eventMapLoading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : eventMapUrl ? (
            <Box
              ref={eventMapImageRef}
              component="img"
              src={eventMapUrl}
              alt={eventMap?.fileName || 'Event map'}
              onLoad={refreshEventMapImageMetrics}
              sx={{
                position: 'absolute',
                ...(eventMapImageMetrics
                  ? {
                      left: eventMapImageMetrics.left,
                      top: eventMapImageMetrics.top,
                      width: eventMapImageMetrics.width,
                      height: eventMapImageMetrics.height,
                    }
                  : {
                      width: '100%',
                      height: '100%',
                    }),
                display: 'block',
                objectFit: 'fill',
                opacity: eventMap?.opacity ?? 0.7,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ height: '100%', p: 3, textAlign: 'center' }}
            >
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#0F172A',
                  color: '#67E8F9',
                }}
              >
                <Image />
              </Box>
              <Stack spacing={0.5}>
                <Typography fontWeight={900} color="#0F172A">
                  Event Map
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No event map uploaded.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                startIcon={<UploadFile />}
                disabled={!canManageEventMap}
                onClick={() => setOpenEventMapManager(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#239AA4',
                }}
              >
                Upload Event Map
              </Button>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            data-event-map-control="true"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              flexWrap: 'wrap',
              // leave room for the zoom buttons pinned top-right
              maxWidth: 'calc(100% - 84px)',
            }}
            useFlexGap
          >
            <Chip
              icon={<Image />}
              label="Event"
              sx={{
                backgroundColor: 'rgba(15,23,42,0.88)',
                color: 'white',
                fontWeight: 900,
                '& .MuiChip-icon': { color: '#67E8F9' },
              }}
            />
            {eventMap?.s3key && (
              <Chip
                icon={<Straighten />}
                label={eventMap.drawnToScale ? 'To scale' : 'Manual positions'}
                sx={{
                  backgroundColor: eventMap.drawnToScale
                    ? 'rgba(0,150,76,0.9)'
                    : 'rgba(154,52,18,0.92)',
                  color: 'white',
                  fontWeight: 900,
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
            {canManageEventMap && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setOpenEventMapManager(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: 'rgba(15,23,42,0.88)',
                }}
              >
                Manage
              </Button>
            )}
            {eventMapUrl && canDispatch && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddLocationAlt />}
                onClick={() => {
                  setAddingPin((prev) => !prev)
                  setArmedResourceID(null)
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: addingPin
                    ? '#239AA4'
                    : 'rgba(15,23,42,0.88)',
                  '&:hover': {
                    backgroundColor: addingPin
                      ? '#1E858E'
                      : 'rgba(15,23,42,0.96)',
                  },
                }}
              >
                {addingPin ? 'Cancel Pin' : 'Add Pin'}
              </Button>
            )}
            {eventMapUrl && eventMapPins.length > 0 && (
              <Autocomplete
                size="small"
                options={eventMapPins}
                getOptionLabel={(pin) => pin.label || ''}
                value={null}
                blurOnSelect
                clearOnBlur
                onChange={(event, pin) => {
                  if (pin) focusPin(pin)
                }}
                renderOption={(props, pin) => {
                  const { key, ...optionProps } = props
                  return (
                    <Box
                      component="li"
                      key={key}
                      {...optionProps}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: pin.color || DEFAULT_PIN_COLOR,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap>
                        {pin.label}
                      </Typography>
                    </Box>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search map pins"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0.5 }}>
                          <Search
                            fontSize="small"
                            sx={{ color: 'rgba(255,255,255,0.72)' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(15,23,42,0.88)',
                        borderRadius: 2,
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255,255,255,0.4)',
                        },
                      },
                      '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator':
                        {
                          color: 'rgba(255,255,255,0.72)',
                        },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
                sx={{ width: 210 }}
              />
            )}
          </Stack>

          {eventMapUrl && (
            <Stack
              data-event-map-control="true"
              spacing={1}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 12,
              }}
            >
              <Tooltip title="Zoom in">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    updateEventMapZoom(eventMapViewport.zoom * 1.2)
                  }}
                  disabled={eventMapViewport.zoom >= EVENT_MAP_MAX_ZOOM - 0.01}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.96)' },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.38)',
                      backgroundColor: 'rgba(15,23,42,0.52)',
                    },
                  }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom out">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    updateEventMapZoom(eventMapViewport.zoom / 1.2)
                  }}
                  disabled={eventMapViewport.zoom <= 1.01}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.96)' },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.38)',
                      backgroundColor: 'rgba(15,23,42,0.52)',
                    },
                  }}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {eventMapPins.map((pin) => (
            <Box
              key={pin.id}
              data-event-map-marker="true"
              onClick={(event) => {
                event.stopPropagation()
                setHighlightedPinID(pin.id)
                if (canDispatch) setPinDialog({ pin })
              }}
              sx={{
                position: 'absolute',
                ...eventMapPositionSx(pin.coordinates),
                // Anchor the icon tip on the pin's coordinates (label sits
                // above the icon inside the marker).
                transform: 'translate(-50%, -100%)',
                zIndex: highlightedPinID === pin.id ? 5 : 4,
                cursor: 'pointer',
              }}
            >
              <MapPinMarker pin={pin} highlighted={highlightedPinID === pin.id} />
            </Box>
          ))}

          {eventMapTreatmentCentres.map((centre) => (
            <Box
              key={String(centre._id)}
              data-event-map-marker="true"
              onClick={(event) => {
                event.stopPropagation()
                onOpenTreatmentCentre?.(String(centre._id))
              }}
              sx={{
                position: 'absolute',
                ...eventMapPositionSx(centre.eventMapCoordinates),
                // Anchor the icon's base on the centre's coordinates (the
                // name/status label only appears on hover).
                transform: 'translate(-50%, -100%)',
                zIndex: 4,
                cursor: onOpenTreatmentCentre ? 'pointer' : 'default',
              }}
            >
              <TreatmentCentreMarker centre={centre} />
            </Box>
          ))}

          {eventMapResources.map((resource) => {
            const color = statusColors[resource.status] || '#239AA4'
            const selected =
              selectedMarker?.type === 'resource' &&
              selectedMarker?.id === resource.id
            const unlocked = unlockedResourceID === resource.id
            const [x, y] = resource.eventMapCoordinates

            return (
              <Box
                key={resource.id}
                data-event-map-marker="true"
                onPointerDown={(event) =>
                  handleEventResourcePointerDown(event, resource)
                }
                onPointerMove={handleEventResourcePointerMove}
                onPointerUp={handleEventResourcePointerUp}
                onPointerCancel={handleEventResourcePointerUp}
                onClick={(event) => {
                  event.stopPropagation()
                  setSelectedMarker({ type: 'resource', id: resource.id })
                  setArmedResourceID(null)
                }}
                sx={{
                  position: 'absolute',
                  ...eventMapPositionSx([x, y]),
                  transform: 'translate(-50%, -50%)',
                  zIndex: selected ? 6 : 5,
                  cursor: unlocked ? 'grab' : 'pointer',
                }}
              >
                <ResourceMarker
                  resource={resource}
                  color={color}
                  selected={selected}
                />
              </Box>
            )
          })}

          {eventMapIncidents.map((incident) => {
            const color =
              priorityColors[incident.priority] || priorityColors.Low
            const selected =
              selectedMarker?.type === 'incident' &&
              selectedMarker?.id === incident.id
            const [x, y] = incident.eventMapCoordinates

            return (
              <Box
                key={incident.id}
                data-event-map-marker="true"
                onClick={(event) => {
                  event.stopPropagation()
                  setSelectedMarker({ type: 'incident', id: incident.id })
                  setSelectedIncident(incident)
                }}
                sx={{
                  position: 'absolute',
                  ...eventMapPositionSx([x, y]),
                  transform: 'translate(-50%, -50%)',
                  zIndex: selected ? 8 : 7,
                  cursor: 'pointer',
                }}
              >
                <IncidentMarker
                  incident={incident}
                  color={color}
                  selected={selected}
                />
              </Box>
            )
          })}

          {armedResourceID && (
            <Chip
              icon={<OpenWith />}
              label={`Click map to place ${resourceMap.get(armedResourceID)?.callSign || 'resource'}`}
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                backgroundColor: 'rgba(15,23,42,0.9)',
                color: 'white',
                fontWeight: 900,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}

          {addingPin && (
            <Chip
              icon={<AddLocationAlt />}
              label="Click the map to place the pin"
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                backgroundColor: 'rgba(15,23,42,0.9)',
                color: 'white',
                fontWeight: 900,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>
      )}

      {mapView === 'overlay' && !scaledEventMap && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            width: '100%',
            height: '100%',
            p: 3,
            backgroundColor: '#0F172A',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(35,154,164,0.18)',
              color: '#67E8F9',
            }}
          >
            <Layers />
          </Box>
          <Typography fontWeight={900} color="white">
            Overlay
          </Typography>
          <Typography variant="body2" color="#CBD5E1" sx={{ maxWidth: 420 }}>
            The overlay lays a to-scale event map over the live map so crews
            can navigate straight to incidents. Upload a map and mark it as
            drawn to scale, then line it up with the site under Manage.
          </Typography>
          {canManageEventMap && (
            <Button
              variant="contained"
              onClick={() => setOpenEventMapManager(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#239AA4',
              }}
            >
              {eventMap?.s3key ? 'Calibrate Event Map' : 'Upload Event Map'}
            </Button>
          )}
        </Stack>
      )}

      {isScaledOverlay && (
        <Chip
          icon={<Layers />}
          label="Site plan overlaid to scale"
          sx={{
            position: 'absolute',
            bottom: 12,
            right: compact ? 12 : 344,
            backgroundColor: 'rgba(15,23,42,0.88)',
            color: 'white',
            fontWeight: 900,
            '& .MuiChip-icon': { color: '#67E8F9' },
          }}
        />
      )}

      {isGpxView && !gpxRoutes.length && (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', p: 3 }}
        >
          <Paper
            elevation={0}
            sx={{
              pointerEvents: 'auto',
              p: 3,
              borderRadius: 3,
              maxWidth: 440,
              textAlign: 'center',
              backgroundColor: 'rgba(15,23,42,0.9)',
              color: 'white',
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(35,154,164,0.18)',
                  color: '#67E8F9',
                }}
              >
                <Route />
              </Box>
              <Typography fontWeight={900}>GPX Routes</Typography>
              <Typography variant="body2" color="#CBD5E1">
                Upload a GPX course file to draw the route on the live map
                with kilometre markers, and track crews along it.
              </Typography>
              {canManageEventMap ? (
                <Button
                  variant="contained"
                  startIcon={<UploadFile />}
                  onClick={() => setOpenGpxManager(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 900,
                    backgroundColor: '#239AA4',
                  }}
                >
                  Add GPX Route
                </Button>
              ) : (
                <Typography variant="caption" color="#94A3B8">
                  A commander can add GPX routes under Manage.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}

      {isGeographicMap && armedResourceID && (
        <Chip
          icon={<OpenWith />}
          label={`Click the map to place ${resourceMap.get(armedResourceID)?.callSign || 'resource'}`}
          onDelete={() => setArmedResourceID(null)}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 56,
            backgroundColor: 'rgba(15,23,42,0.9)',
            color: 'white',
            fontWeight: 900,
            '& .MuiChip-icon': { color: 'white' },
            '& .MuiChip-deleteIcon': {
              color: '#CBD5E1',
              '&:hover': { color: 'white' },
            },
          }}
        />
      )}

      {isGpxView && gpxRoutes.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            // Compact keeps the attribution bar in the same corner — sit
            // above it; full layout's side panel already clears it.
            bottom: compact ? 40 : 12,
            right: compact ? 12 : 344,
            maxWidth: 300,
            borderRadius: 3,
            p: 1,
            backgroundColor: 'rgba(15,23,42,0.88)',
            color: 'white',
          }}
        >
          <Stack spacing={0.25}>
            {gpxRoutes.map((route) => {
              const hidden = hiddenGpxRouteIDs.has(String(route._id))
              return (
                <Stack
                  key={route._id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: route.color || GPX_ROUTE_COLORS[0],
                      opacity: hidden ? 0.35 : 1,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={900}
                    noWrap
                    sx={{ flex: 1, opacity: hidden ? 0.5 : 1 }}
                  >
                    {route.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#CBD5E1' }}>
                    {formatRouteDistance(route.distanceMeters)}
                  </Typography>
                  <Tooltip title={hidden ? 'Show route' : 'Hide route'}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setHiddenGpxRouteIDs((prev) => {
                          const next = new Set(prev)
                          const id = String(route._id)
                          if (next.has(id)) next.delete(id)
                          else next.add(id)
                          return next
                        })
                      }
                      sx={{ color: 'white', p: 0.25 }}
                    >
                      {hidden ? (
                        <VisibilityOff fontSize="inherit" />
                      ) : (
                        <Visibility fontSize="inherit" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            })}
            {canManageEventMap && (
              <Button
                size="small"
                onClick={() => setOpenGpxManager(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  color: '#67E8F9',
                  alignSelf: 'flex-start',
                }}
              >
                Manage Routes
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {isGeographicMap && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: compact ? 12 : 'auto',
            flexWrap: 'wrap',
          }}
          useFlexGap
        >
          <Chip
            icon={<MyLocation />}
            label={`${mappedResources.length} resources`}
            sx={{
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              fontWeight: 900,
              '& .MuiChip-icon': { color: '#67E8F9' },
            }}
          />
          <Chip
            icon={<ReportProblem />}
            label={`${mappedIncidents.length} incidents`}
            sx={{
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              fontWeight: 900,
              '& .MuiChip-icon': { color: '#FCA5A5' },
            }}
          />
          {(unmappedResources > 0 || unmappedIncidents > 0) && (
            <Chip
              icon={<LocationOff />}
              label={`${unmappedResources + unmappedIncidents} unmapped`}
              sx={{
                backgroundColor: 'rgba(154,52,18,0.92)',
                color: 'white',
                fontWeight: 900,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
          {isScaledOverlay && canManageEventMap && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setOpenEventMapManager(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: 'rgba(15,23,42,0.88)',
              }}
            >
              Manage
            </Button>
          )}
        </Stack>
      )}

      {isGeographicMap && (
        <Tooltip title="Fit map">
          <IconButton
            onClick={fitToMarkers}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(15,23,42,0.96)' },
            }}
          >
            <CenterFocusStrong />
          </IconButton>
        </Tooltip>
      )}

      {!compact && (isGeographicMap || mapView === 'event') && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: 64,
            right: 12,
            bottom: 12,
            width: 320,
            // Phones: a 320px side panel would cover the whole map; the
            // resources list lives in the Resources panel there instead.
            display: { xs: 'none', md: 'block' },
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: 'rgba(255,255,255,0.96)',
            border: '1px solid rgba(15,23,42,0.08)',
          }}
        >
          <Stack sx={{ height: '100%' }}>
            <Box sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <NotificationsActive sx={{ color: '#239AA4' }} />
                <Typography fontWeight={900}>Resources</Typography>
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={1} sx={{ p: 1.5, overflow: 'auto' }}>
              {resources.map((resource) => (
                <Box
                  key={resource.id}
                  draggable={mapView === 'event'}
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/resource-id', resource.id)
                    event.dataTransfer.effectAllowed = 'move'
                  }}
                  onClick={() => focusResource(resource)}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border:
                      selectedMarker?.type === 'resource' &&
                      selectedMarker?.id === resource.id
                        ? '2px solid #239AA4'
                        : '1px solid #E2E8F0',
                    backgroundColor:
                      armedResourceID === resource.id ? '#E6F7F9' : '#F8FAFC',
                    cursor: 'pointer',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <Stack minWidth={0} flex={1}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography fontWeight={900} noWrap>
                          {resource.callSign}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {(resource.users || [])
                          .map((user) => user.name)
                          .join(', ') || 'No crew assigned'}
                      </Typography>
                    </Stack>
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Chip
                        label={resource.status || 'Unknown'}
                        size="small"
                        sx={{
                          backgroundColor:
                            statusColors[resource.status] || '#64748B',
                          color: 'white',
                          fontWeight: 800,
                        }}
                      />
                      <Chip
                        icon={
                          mapView === 'event' ? (
                            resource.eventMapCoordinates?.length === 2 ? (
                              <Room />
                            ) : (
                              <OpenWith />
                            )
                          ) : parseCoordinatePair(resource.location) ||
                            parseCoordinatePair(
                              resource.linkedVehicle?.location,
                            ) ? (
                            <Room />
                          ) : parseCoordinatePair(resource.manualCoordinates) ||
                            eventMapToLngLat(
                              eventMap,
                              resource.eventMapCoordinates,
                            ) ? (
                            <Room />
                          ) : (
                            <LocationOff />
                          )
                        }
                        label={
                          mapView === 'event'
                            ? resource.eventMapCoordinates?.length === 2
                              ? 'Placed'
                              : 'Drag'
                            : parseCoordinatePair(resource.location) ||
                                parseCoordinatePair(
                                  resource.linkedVehicle?.location,
                                )
                              ? 'GPS'
                              : parseCoordinatePair(
                                    resource.manualCoordinates,
                                  ) ||
                                  eventMapToLngLat(
                                    eventMap,
                                    resource.eventMapCoordinates,
                                  )
                                ? 'Placed'
                                : 'No loc'
                        }
                        size="small"
                        sx={{
                          maxWidth: 84,
                          '& .MuiChip-icon': { fontSize: 15 },
                        }}
                      />
                    </Stack>
                    <Tooltip title="Message this resource">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          openResourceChat?.(resource)
                        }}
                        sx={{
                          color: '#239AA4',
                          backgroundColor: 'rgba(35,154,164,0.12)',
                          '&:hover': {
                            backgroundColor: 'rgba(35,154,164,0.24)',
                          },
                        }}
                      >
                        <Badge
                          color="error"
                          badgeContent={
                            unreadByResource.get(
                              String(resource._id ?? resource.id),
                            ) ?? 0
                          }
                          overlap="circular"
                        >
                          <Forum fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Paper>
      )}

      {(isGeographicMap || mapView === 'event') &&
        (selectedResource || selectedIncident) &&
        !(isGeographicMap && armedResourceID) && (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              // Clear of the zoom NavigationControl pinned bottom-left.
              left: 56,
              bottom: 12,
              maxWidth: compact ? 'calc(100% - 68px)' : 420,
              p: 1.5,
              borderRadius: 3,
              backgroundColor: 'rgba(15,23,42,0.92)',
              color: 'white',
            }}
          >
            {selectedResource && (
              <Stack spacing={0.5}>
                <Typography fontWeight={900} color="white">
                  {selectedResource.callSign}
                </Typography>
                <Typography variant="body2" color="#CBD5E1">
                  {selectedResource.status || 'Unknown'} ·{' '}
                  {selectedResource.type || 'Resource'}
                </Typography>
                {selectedResource.note && (
                  <Typography variant="caption" color="#E2E8F0">
                    {selectedResource.note}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Forum />}
                    onClick={() => openResourceChat?.(selectedResource)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      backgroundColor: '#239AA4',
                    }}
                  >
                    Message
                  </Button>
                  {isGeographicMap &&
                    canDispatch &&
                    !selectedResourceLiveGps && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<OpenWith />}
                        onClick={() =>
                          setArmedResourceID(selectedResource.id)
                        }
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          backgroundColor: '#64748B',
                        }}
                      >
                        {selectedResourceMappedLngLat
                          ? 'Move on Map'
                          : 'Place on Map'}
                      </Button>
                    )}
                  {usesManualEventMap &&
                    selectedResourceEventMapCoordinates?.length === 2 && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          setUnlockedResourceID((prev) =>
                            prev === selectedResource.id
                              ? null
                              : selectedResource.id,
                          )
                        }
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          backgroundColor:
                            unlockedResourceID === selectedResource.id
                              ? '#E32525'
                              : '#64748B',
                        }}
                      >
                        {unlockedResourceID === selectedResource.id
                          ? 'Lock'
                          : 'Unlock'}
                      </Button>
                    )}
                </Stack>
              </Stack>
            )}
            {selectedIncident && (
              <Stack spacing={0.5}>
                <Typography fontWeight={900} color="white">
                  Incident {selectedIncident.incidentNumber}
                </Typography>
                <Typography variant="body2" color="#CBD5E1">
                  {selectedIncident.location || 'Location not recorded'}
                </Typography>
                <Typography variant="caption" color="#E2E8F0">
                  {selectedIncident.priority || 'Low'} ·{' '}
                  {selectedIncident.type || 'Other'}
                </Typography>
              </Stack>
            )}
          </Paper>
        )}

      <EventMapManager
        open={openEventMapManager}
        onClose={() => setOpenEventMapManager(false)}
        controlRoomID={controlRoomID}
        eventMap={eventMap}
        onSaved={onEventMapSaved}
        initialSearch={linkedEvent?.eventLocation}
        initialCenter={eventCentre}
      />

      <GpxRouteManager
        open={openGpxManager}
        onClose={() => setOpenGpxManager(false)}
        controlRoomID={controlRoomID}
        gpxRoutes={gpxRoutes}
        onSaved={(routes) => onGpxRoutesSaved?.(routes)}
      />

      <MapPinDialog
        open={!!pinDialog}
        pin={pinDialog?.pin}
        onSave={savePinDialog}
        onDelete={deletePinDialog}
        onClose={() => setPinDialog(null)}
      />
    </Paper>
  )
}

export default EventControlMap
